import { API_TYPES, WorkerRequirementDomain } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { type UseGetQueryOptions } from "@src/appV2/api";
import { type AxiosError } from "axios";

import { mergeWorkerRequirementWithIncludedResources } from "../api/mergeWorkerRequirementWithIncludedResources";
import { type WorkerRequirement } from "../api/types";
import { useGetWorkerRequirements } from "../api/useGetWorkerRequirements";
import { useGetMiscRequirement } from "../api/wrappers/useGetMiscRequirement";
import { useGetWorkerLicensesAllowedForDocuments } from "../useGetWorkerLicensesAllowedForDocuments";
import { createMiscWorkerRequirement } from "./Workflows/Misc/utils";

interface UseGetDocumentDetailsPageRequirementParams {
  requirementId: string;
  options?: UseGetQueryOptions<any, AxiosError, WorkerRequirement[]>;
}

/**
 * We fetch requirement details from worker-bff for all types of requirements
 * However, miscellaneous requirements won't be returned from this API
 * That's why we have this fallback to other API, to specifically handle miscellaneous requirements
 */
export function useGetDocumentDetailsPageRequirement(
  params: UseGetDocumentDetailsPageRequirementParams
) {
  const { requirementId, options } = params;

  const { licenses: availableLicenses, isLoaded: areLicensesLoaded } =
    useGetWorkerLicensesAllowedForDocuments();

  const {
    data: workerRequirementData,
    isLoading: isLoadingWorkerRequirement,
    isSuccess: isSuccessWorkerRequirement,
    isError: isErrorWorkerRequirement,
    refetch: refetchWorkerRequirement,
  } = useGetWorkerRequirements(
    {
      filter: {
        qualifications: availableLicenses,
        domains: [WorkerRequirementDomain.HIRING, WorkerRequirementDomain.FLEX],
        "requirement.id": [requirementId],
      },
      include: [API_TYPES.requirements, API_TYPES.requirementMappings, API_TYPES.workerDocuments],
    },
    {
      select: mergeWorkerRequirementWithIncludedResources,
      enabled: areLicensesLoaded && (options?.enabled ?? true),
      ...options,
    }
  );

  const workerRequirement = workerRequirementData?.find(
    (requirement) => requirement.requirementId === requirementId
  );

  const {
    data: miscRequirement,
    isInitialLoading: isLoadingMisc,
    isError: isErrorMisc,
  } = useGetMiscRequirement({
    // if we fetched worker requirements and it's given id is not there,
    // we will load the miscellaneous because it's the only requirement that
    // worker-requirements api does not return
    enabled: isSuccessWorkerRequirement && !isDefined(workerRequirement),
  });

  // we need to check if the id from path matches fetched misc requirement id because
  // otherwise we would fall back every invalid requirement id to treat as miscellaneous
  const isMiscRequirement = isDefined(miscRequirement) && miscRequirement.id === requirementId;

  const miscWorkerRequirement = isMiscRequirement
    ? createMiscWorkerRequirement({
        requirementId: miscRequirement.id,
        qualifications: availableLicenses,
        instructions: miscRequirement.attributes.instructions,
      })
    : undefined;

  return {
    isLoading: isLoadingWorkerRequirement || isLoadingMisc || !areLicensesLoaded,
    isError: isErrorWorkerRequirement || isErrorMisc,
    requirement: workerRequirement ?? miscWorkerRequirement,
    refetchWorkerRequirement,
  };
}
